import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Row, Col, Form, InputGroup, Alert, Modal } from 'react-bootstrap';
import { Search, Edit, PlusCircle, RefreshCw, Image, Package, Download } from 'lucide-react';
import { getAllProducts, deleteProduct, deleteMultipleProducts } from '../../api/products';
import useAuth from '../../hooks/useAuth';
import ProductDetails from './ProductDetails'; // Importez le nouveau composant
import { getDistributorPricesCurrentUser } from '../../api/partner';

// Configuration - Ajustez cette URL selon votre configuration backend
//const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://senoscar.site';
//const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/senoscar';



const ProductList = ({ onAddProduct, onEditProduct }) => {
    const { isAdmin, isManager, isSalesRep, isDistributor, user } = useAuth();
    const hasStockAccess = isAdmin || isManager || isSalesRep;
    const [priceList, setPriceList] = useState([]);
    const [loadingPrices, setLoadingPrices] = useState(true);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState(['name', 'sku', 'regular_price', 'stock_quantity', 'category', 'dimensions']);


    // Définition des colonnes disponibles pour l'export
    const availableColumns = [
        { key: 'name', label: 'name', valueGetter: (product) => product.name },
        { key: 'sku', label: 'sku', valueGetter: (product) => product.sku },
        { key: 'basePrice', label: 'basePrice', valueGetter: (product) => product.regular_price },
        { key: 'priceWithVat', label: 'priceWithVat', valueGetter: (product) => (product.regular_price * 1.18).toFixed(2) },
        { key: 'stockQuantity', label: 'stockQuantity', valueGetter: (product) => product.stock_quantity },
        { key: 'category', label: 'category', valueGetter: (product) => product.category || '' },
        { key: 'dimensions', label: 'dimensions', valueGetter: (product) => product.dimensions || '' },
        { key: 'createdAt', label: 'createdAt', valueGetter: (product) => product.createdAt ? new Date(product.createdAt).toLocaleDateString() : '' },
        { key: 'updatedAt', label: 'updatedAt', valueGetter: (product) => product.updatedAt ? new Date(product.updatedAt).toLocaleDateString() : '' }
    ];

    const exportToCSV = () => {
        // Déterminer quels produits exporter
        let productsToExport = [];
        if (selectedProducts.length > 0) {
            productsToExport = filteredProducts.filter(product => selectedProducts.includes(product.id));
        } else {
            productsToExport = filteredProducts;
        }

        if (productsToExport.length === 0) {
            alert('Aucun produit à exporter');
            return;
        }

        // Filtrer les colonnes sélectionnées
        const selectedColumnObjects = availableColumns.filter(col => selectedColumns.includes(col.key));

        // Créer les en-têtes
        const headers = selectedColumnObjects.map(col => col.label);

        // Créer les lignes de données
        const rows = productsToExport.map(product => {
            return selectedColumnObjects.map(col => col.valueGetter(product));
        });

        // Utiliser point-virgule comme séparateur pour Excel français
        const separator = ';';

        // Créer le contenu CSV
        let csvContent = '\uFEFF'; // BOM pour Excel

        // Ajouter les en-têtes
        csvContent += headers.join(separator) + '\n';

        // Ajouter les données
        rows.forEach(row => {
            const escapedRow = row.map(value => {
                const strValue = String(value);
                if (strValue.includes(separator) || strValue.includes('"') || strValue.includes('\n')) {
                    return `"${strValue.replace(/"/g, '""')}"`;
                }
                return strValue;
            });

            csvContent += escapedRow.join(separator) + '\n';
        });

        // Créer et télécharger le fichier
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `products_export_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setShowExportModal(false);
    };

    const handleColumnSelection = (columnKey) => {
        if (selectedColumns.includes(columnKey)) {
            setSelectedColumns(selectedColumns.filter(key => key !== columnKey));
        } else {
            setSelectedColumns([...selectedColumns, columnKey]);
        }
    };

    const ExportModal = () => (
        <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Options d'export</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <h6>Produits à exporter :</h6>
                    <p className="text-muted">
                        {selectedProducts.length > 0
                            ? `${selectedProducts.length} produit(s) sélectionné(s)`
                            : `Tous les produits filtrés (${filteredProducts.length})`
                        }
                    </p>
                </div>

                <div>
                    <h6>Colonnes à inclure :</h6>
                    {availableColumns.map(column => (
                        <Form.Check
                            key={column.key}
                            type="checkbox"
                            id={`column-${column.key}`}
                            label={column.label}
                            checked={selectedColumns.includes(column.key)}
                            onChange={() => handleColumnSelection(column.key)}
                            className="mb-2"
                        />
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                    Annuler
                </Button>
                <Button variant="primary" onClick={exportToCSV}>
                    Exporter
                </Button>
            </Modal.Footer>
        </Modal>
    );


    // États pour la modal de détails du produit
    const [showDetails, setShowDetails] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);

    const canModify = isAdmin || isManager;

    useEffect(() => {
        const fetchPrices = async () => {
            if (isDistributor && user?.id) {
                try {
                    const prices = await getDistributorPricesCurrentUser(user.id);
                    setPriceList(prices);
                } catch (err) {
                    console.error("Erreur de chargement des prix du distributeur", err);
                } finally {
                    setLoadingPrices(false);
                }
            } else {
                setLoadingPrices(false);
            }
        };

        fetchPrices();
    }, [isDistributor, user]);

    console.log('priceList updated:', priceList);

    const getDistributorPrice = (productId) => {
        const entry = priceList.find(p => p.product.id === productId);
        return entry ? entry.price : null;
    };


   useEffect(() => {
    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getAllProducts();

            // Trier par nom décroissant
            const sortedData = data.sort((a, b) => {
                if (b.name < a.name) return 1;
                if (b.name > a.name) return -1;
                return 0;
            });

            setProducts(sortedData);
            setFilteredProducts(sortedData);
        } catch (err) {
            setError('Failed to load products. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    fetchProducts();
}, [refreshTrigger]);


    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredProducts(products);
        } else {
            const filtered = products.filter(product =>
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredProducts(filtered);
        }
    }, [searchTerm, products]);

    useEffect(() => {
        // When filtered products change, reset selection
        setSelectedProducts([]);
        setSelectAll(false);
    }, [filteredProducts]);

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                await deleteProduct(id);
                setProducts(products.filter(product => product.id !== id));
                setFilteredProducts(filteredProducts.filter(product => product.id !== id));
            } catch (err) {
                setError('Failed to delete product. Please try again.');
                console.error(err);
            }
        }
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);

        if (isChecked) {
            setSelectedProducts(filteredProducts.map(product => product.id));
        } else {
            setSelectedProducts([]);
        }
    };

    const handleSelectProduct = (id) => {
        if (selectedProducts.includes(id)) {
            setSelectedProducts(selectedProducts.filter(productId => productId !== id));
        } else {
            setSelectedProducts([...selectedProducts, id]);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedProducts.length === 0) return;

        if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} selected products?`)) {
            try {
                await deleteMultipleProducts(selectedProducts);

                // Update products list
                const updatedProducts = products.filter(product => !selectedProducts.includes(product.id));
                setProducts(updatedProducts);
                setFilteredProducts(filteredProducts.filter(product => !selectedProducts.includes(product.id)));

                // Reset selection
                setSelectedProducts([]);
                setSelectAll(false);
            } catch (err) {
                setError('Failed to delete selected products. Please try again.');
                console.error(err);
            }
        }
    };

    // Nouvelle fonction pour ouvrir la modal de détails
    const handleViewProduct = (id) => {
        setSelectedProductId(id);
        setShowDetails(true);
    };

    // Helper function to get full image URL
    const getFullImageUrl = (path) => {
        if (!path) return null;

        // Si le chemin commence déjà par http ou https, c'est une URL complète
        if (path.startsWith('http://') || path.startsWith('https://')) {
            console.log("1111111111111", path);

            return path;
        }

        // Si le chemin commence par '/', on l'ajoute tel quel à l'URL de base
        if (path.startsWith('/')) {
            console.log("222222222", path);
            return `${API_BASE_URL}${path}`;

        }

        // Sinon, on ajoute un '/' entre l'URL de base et le chemin
        console.log("333333333333", path);
        return `${API_BASE_URL}/${path}`;
    };

    // Mis à jour uniquement la fonction getMainImageUrl dans ProductList.jsx

    const getMainImageUrl = (product) => {
        if (!product.images || product.images.length === 0) {
            return null;
        }

        const mainImage = product.images.find(img => img.mainImage);
        const path = mainImage ? mainImage.path : product.images[0].path;

        // Traiter l'URL correctement selon le format stocké en base
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        // Si le chemin commence par "products/"
        // if (path.startsWith('products/')) {
        //     // Garder le chemin tel quel car le FileController est mappé sur /products/...
        //     return `${API_BASE_URL}/${path}`;
        // }

        // Par sécurité, si le format est différent
        return `${API_BASE_URL}/uploads/${path}`;
        //return `${API_BASE_URL}/${path}`;

    };


    return (
        <>
            <Card className="shadow-sm">
                <Card.Header>
                    <Row className="align-items-center">
                        <Col>
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-success"
                                size="sm"
                                className="me-2"
                                onClick={() => setShowExportModal(true)}
                            >
                                <Download size={16} className="me-1" />
                                Exporter
                            </Button>
                            {canModify && (
                                <Button
                                    variant="success"
                                    size="sm"
                                    className="me-2"
                                    onClick={onAddProduct}
                                >
                                    <PlusCircle size={16} className="me-1" />
                                    Ajouter Produit
                                </Button>
                            )}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRefresh}
                            >
                                <RefreshCw size={16} />
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Row className="mb-3">
                        <Col md={8}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Rechercher un produit par son nom, réf ou catégorie..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>

                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2">Produits en chargement...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table striped hover>
                                <thead>
                                    <tr>
                                        {canModify && (
                                            <th width="50">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectAll}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                        )}
                                        <th width="80">Image</th>
                                        <th>Nom</th>
                                        <th>Référence</th>
                                        <th>Catégorie</th>
                                        <th>Prix</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
                                        filteredProducts.map(product => (
                                            <tr key={product.id}>
                                                {canModify && (
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selectedProducts.includes(product.id)}
                                                            onChange={() => handleSelectProduct(product.id)}
                                                        />
                                                    </td>
                                                )}
                                                <td>
                                                    {getMainImageUrl(product) ? (
                                                        <div style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            borderRadius: '4px',
                                                            overflow: 'hidden',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}
                                                            onClick={() => handleViewProduct(product.id)}>
                                                            <img
                                                                src={getMainImageUrl(product)}
                                                                alt={product.name}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                                onError={(e) => {
                                                                    console.log("Erreur de chargement pour l'image:", getMainImageUrl(product));
                                                                    e.target.onError = null;

                                                                    // Remplacer l'image par une icône sans modifier innerHTML
                                                                    e.target.style.display = 'none';

                                                                    // Créer et ajouter l'icône de remplacement de manière sécurisée
                                                                    const iconContainer = document.createElement('div');
                                                                    iconContainer.className = 'd-flex justify-content-center align-items-center w-100 h-100';
                                                                    iconContainer.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/></svg>';

                                                                    // Ajouter l'icône au parent de l'image
                                                                    e.target.parentNode.appendChild(iconContainer);
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="d-flex justify-content-center align-items-center bg-light"
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleViewProduct(product.id)}
                                                        >
                                                            <Package size={20} className="text-muted" />
                                                        </div>
                                                    )}
                                                </td>


                                                <td>{product.name}</td>
                                                <td>{product.sku}</td>
                                                <td>{product.category || '-'}</td>
                                                <td>{product.regular_price.toFixed()} F</td>


                                                <td>
                                                    <Button
                                                        variant="outline-info"
                                                        size="sm"
                                                        className="me-1"
                                                        onClick={() => handleViewProduct(product.id)}
                                                    >
                                                        <Image size={16} />
                                                    </Button>
                                                    {canModify && (
                                                        <>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-1"
                                                                onClick={() => onEditProduct(product.id)}
                                                            >
                                                                <Edit size={16} />
                                                            </Button>

                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={canModify ? "8" : "7"} className="text-center py-3">
                                                Aucun produit trouvé
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card >

            {/* Composant de détails du produit */}
            < ProductDetails
                productId={selectedProductId}
                show={showDetails}
                onHide={() => setShowDetails(false)}
            />
            <ExportModal />
        </>
    );
};

export default ProductList;